import { v4 as uuidv4 } from 'uuid';
import { groupMessagesByDay } from '@zonofi/chats-common/functions/group-messages-by-day';
import { useGetChannelDetails } from '@zonofi/chats-common/hooks/get-channel-details';
import { usePublishMessage } from '@zonofi/chats-common/hooks/publish-message';
import { MessageEnvelope } from '@zonofi/common/schema/message/inferface';
import { MessageType } from '@zonofi/common/schema/message/enum';
import {
  CustomerSearchResponse,
  dispatch,
  getChannelMessages,
  getCurrentChannelId,
  getWorkspace,
  getWorkspaceId,
  updateCurrentChannel,
  useAppSelector,
  useCreateTrialCampaignMutation,
  useGetAllCampaignsMutation,
  useLazyGetCampaignCustomersQuery,
  useLazySearchCustomerQuery,
  updateShowTypingIndicator,
  upsertMessage,
  getUserId,
  getFeedbackCount,
  getShowAgentWorkingModal,
  finService,
  TrackEventType,
  updateCurrentTooltipId,
  updateCurrentTooltipIndex,
  updateIsJTBDTourActive,
  updateFeedbackGoodCount,
  updateFeedbackBadCount,
  getShowGetOnWhatsappStrip,
  updateQuickUserActions,
  updateShowGetOnWhatsappStrip,
  getQuickUserActions,
  useLazyGetCustomerListQuery,
} from '@zonofi/seller-rtk-store';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMessageListCore } from '@zonofi/chats-common/components/message-list';
import { getMessageDayKey } from '@zonofi/common/functions/message-list-helpers/message-date-time';
// import { MenuItem } from '../../../common/interface';

interface useMobileWhatsappSimulatorProps {
  setContentIndex?: (val: number) => void;
}

export const useMobileWhatsappSimulator = ({
  setContentIndex,
}: useMobileWhatsappSimulatorProps) => {
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [customer, setCustomer] = useState<CustomerSearchResponse | null>(null);
  const [msgText, setMsgText] = useState<string>('');
  const [quickReply, setQuickReply] = useState<string>('');
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [msgId, setMsgId] = useState<string>('');
  const [openWelcomeMenu, setOpenWelcomeMenu] = useState<boolean>(false);
  const feedbackCount = useAppSelector(getFeedbackCount);
  const showAgentWorkingModal = useAppSelector(getShowAgentWorkingModal);
  const showGetOnWhatsappStrip = useAppSelector(getShowGetOnWhatsappStrip);
  const channelId = useAppSelector(getCurrentChannelId);
  const messages = useAppSelector(
    getChannelMessages(channelId)
  ) as MessageEnvelope[];
  const workspace = useAppSelector(getWorkspace);
  const workspaceId = useAppSelector(getWorkspaceId);
  const userId = useAppSelector(getUserId);
  const quickUserActions = useAppSelector(getQuickUserActions);
  const [getCampaignsListReq, getCampaignsListRes] =
    useGetAllCampaignsMutation();
  const [getCampaignCustomersReq, getCampaignCustomersRes] =
    useLazyGetCampaignCustomersQuery();
  const [getCustomerDetailsReq, getCustomerDetailsRes] =
    useLazySearchCustomerQuery();
  const [createTrialCampaignReq, createTrialCampaignRes] =
    useCreateTrialCampaignMutation();
  const [getAllCustomersReq, getAllCustomersRes] =
    useLazyGetCustomerListQuery();
  const { publishWhatsappMessage } = usePublishMessage();

  useMessageListCore({ channelId });

  const userActions = [
    {
      label: 'Send Hi 👋',
      onClick: () => {
        sendSampleQuestion('Hi 👋');
      },
      isUsed: quickUserActions?.sentHi,
    },
    {
      label: 'View Catalogue 📖',
      onClick: () => {
        sendSampleQuestion('View Catalogue 📖');
      },
      isUsed: quickUserActions?.viewedCatalogue,
    },
  ];

  const { getChannelDetailsByCustomer } = useGetChannelDetails({
    onSuccess: (data) => {
      dispatch(updateCurrentChannel(data?.id || ''));
    },
  });

  const createTrialCampaign = () => {
    createTrialCampaignReq({
      params: {
        sellerWorkspaceId: workspaceId,
        doPublish: false,
      },
    });
  };

  const getCampaignCustomers = (id: string) => {
    getCampaignCustomersReq({
      id: id,
      workspaceId: workspaceId,
      sellerWorkspaceId: workspaceId,
      pageNo: 1,
      pageSize: 20,
    });
  };

  const groupedMessages: (string | MessageEnvelope)[] = useMemo(() => {
    if (!messages?.length) return [];

    const result: (string | MessageEnvelope)[] = [];

    const visibleMessages = messages.filter((message) =>
      isEmpty(Object.keys(message.actions?.['deleted'] || {}))
    );

    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      const message = visibleMessages[i];

      const nextOlderMessage = i > 0 ? visibleMessages[i - 1] : undefined;

      // ✅ push message (same as core, minus bucket)
      result.push(message);

      // ✅ replicate day grouping logic from core
      const currentDayKey = getMessageDayKey(message);
      const nextOlderDayKey = nextOlderMessage
        ? getMessageDayKey(nextOlderMessage)
        : undefined;

      const hasDayChanged =
        !nextOlderMessage || currentDayKey !== nextOlderDayKey;

      if (hasDayChanged) {
        result.push(currentDayKey); // 👈 string separator (compat)
      }
    }

    return result;
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        if (!isEmpty(msgText.trim())) {
          sendWhatsappMessage();
        }
      }
    }
  };

  const onOpenMenu = () => {
    setOpenMenu(true);
  };

  const onCloseMenu = () => {
    setOpenMenu(false);
  };

  const onOpenWelcomeMenu = () => {
    finService.trackEvent(TrackEventType.SIGNUP_SIMULATOR_PLUS_CLICK, {
      name: customer?.name || '',
      customerPhone: customer?.telephone?.slice(1) || '',
    });
    setOpenWelcomeMenu(true);
  };

  const onCloseWelcomeMenu = () => {
    setOpenWelcomeMenu(false);
  };

  const onMenuClick = (menuItems: any[], msgId: string) => {
    setMenuItems(menuItems);
    onOpenMenu();
    setMsgId(msgId);
    dispatch(updateCurrentTooltipIndex(1));
  };

  const onMenuItemClick = (quickReply: string) => {
    finService.trackEvent(TrackEventType.SIGNUP_SIMULATOR_SELECT_MENU_OPTION, {
      name: customer?.name || '',
      customerPhone: customer?.telephone?.slice(1) || '',
      selectedMenuItem: quickReply,
    });
    setQuickReply(quickReply);
  };

  const onSendMenuClick = () => {
    publishWhatsappMessage({
      name: customer?.name || '',
      customerPhone: customer?.telephone || '',
      text: quickReply,
      contextId: msgId,
    });
    setMsgId('');
    setQuickReply('');
    onCloseMenu();
  };

  const onSendWelcomeMenuOption = () => {
    publishWhatsappMessage({
      name: customer?.name || '',
      customerPhone: customer?.telephone || '',
      text: quickReply,
      intent: 'trial',
    });
    finService.trackEvent(TrackEventType.SIGNUP_SIMULATOR_SEND_MENU_OPTION, {
      name: customer?.name || '',
      customerPhone: customer?.telephone || '',
      sentMenuItem: quickReply,
      intent: 'trial',
    });
    setQuickReply('');
    onCloseWelcomeMenu();
  };

  const publishQuickReply = (contextId: string, text: string) => {
    publishWhatsappMessage({
      name: customer?.name || '',
      customerPhone: customer?.telephone || '',
      text: text,
      contextId: contextId,
    });
  };

  const publishReaction = (
    contextId: string,
    reaction: string,
    replyText: string
  ) => {
    if (reaction === '👍') {
      dispatch(updateFeedbackGoodCount());
    } else if (reaction === '👎') {
      dispatch(updateFeedbackBadCount());
    }
    publishWhatsappMessage({
      name: customer?.name || '',
      customerPhone: customer?.telephone || '',
      type: 'reaction',
      intent: 'trial',
      contextId: contextId,
      reaction: reaction,
      replyText: replyText,
      passContextId: false,
    });
  };

  const updateReduxStateForMessage = (): string => {
    const msgUUID = uuidv4();
    const msg = {
      channel: channelId,
      message: {
        waConfigId: 'zotok-mock',
        _id: msgUUID,
        waConfig: {
          id: 'zotok-mock',
          displayName: 'ZoTok Mock',
          whatsappPhoneNo: workspace?.user?.mobile?.slice(1),
          provider: 'zotok',
          wabaId: 'zotokmockwabaid',
        },
        type: 'text',
        text: msgText?.trim(),
        cAt: Date.now() + '0000',
        user: {
          fName: workspace?.spaceName || '',
        },
        chnType: 'C',
        _v: 2,
        source: 'whatsapp',
        intent: '',
      },
      messageType: null,
      uuid: uuidv4(),
      timetoken: Date.now() + '0000',
    };
    dispatch(upsertMessage({ ...msg }));
    return msgUUID;
  };

  const sendWhatsappMessage = () => {
    if (!isEmpty(msgText.trim())) {
      const msgUUID = updateReduxStateForMessage();
      finService.trackEvent(TrackEventType.SIGNUP_SIMULATOR_SEND_MESSAGE, {
        name: customer?.name || '',
        customerPhone: customer?.telephone?.slice(1) || '',
        messageText: msgText.trim(),
        intent: 'trial',
        _id: msgUUID,
      });
      if (msgText.trim() === 'Hi 👋' && !quickUserActions?.sentHi) {
        dispatch(updateShowGetOnWhatsappStrip(true));
        dispatch(updateQuickUserActions({ key: 'sentHi', value: true }));
      }
      if (
        msgText.trim() === 'View Catalogue 📖' &&
        !quickUserActions?.viewedCatalogue
      ) {
        dispatch(updateShowGetOnWhatsappStrip(true));
        dispatch(
          updateQuickUserActions({ key: 'viewedCatalogue', value: true })
        );
      }
      publishWhatsappMessage({
        name: customer?.name || '',
        customerPhone: customer?.telephone || '',
        text: msgText.trim(),
        intent: 'trial',
        _id: msgUUID,
      });
      setMsgText('');
      scrollToBottom();

      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set typing indicator to true
      dispatch(updateShowTypingIndicator(true));

      // Set timeout to reset typing indicator after 15 seconds
      typingTimeoutRef.current = setTimeout(() => {
        dispatch(updateShowTypingIndicator(false));
        typingTimeoutRef.current = null;
      }, 15000);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const sendSampleQuestion = (val: string) => {
    setMsgText(val);
    inputRef?.current?.focus();
  };

  const getCampaignsList = () => {
    getCampaignsListReq({
      workspaceId: workspaceId,
      sellerWorkspaceId: workspaceId,
      body: {},
    });
  };

  useEffect(() => {
    getCampaignsList();
  }, [workspaceId]);

  useEffect(() => {
    if (getCampaignsListRes?.isSuccess && getCampaignsListRes?.data) {
      const data = getCampaignsListRes?.data?.data || [];
      const trialCampaign = data?.find(
        (item) => item?.name === 'Trial Campaign'
      );
      if (trialCampaign) {
        getCampaignCustomers(trialCampaign?.id ?? '');
      } else {
        createTrialCampaign();
      }
    }
  }, [getCampaignsListRes]);

  useEffect(() => {
    if (
      getCampaignCustomersRes?.isSuccess &&
      getCampaignCustomersRes?.data?.data?.customers?.length === 0
    ) {
      const data = getCampaignCustomersRes?.data?.data;
      const isCustomerExists = data?.customers?.length > 0;
      if (!isCustomerExists) {
        createTrialCampaign();
      }
    } else if (
      getCampaignCustomersRes?.isSuccess &&
      getCampaignCustomersRes?.data
    ) {
      const data = getCampaignCustomersRes?.data?.data;
      const customerId = data?.customers?.[0]?.inviteId ?? '';

      getCustomerDetailsReq({
        workspaceId: workspaceId,
        inviteId: customerId,
        includeCFA: false,
        includeInactive: false,
      });
    }
  }, [getCampaignCustomersRes]);

  useEffect(() => {
    if (getCustomerDetailsRes?.isSuccess) {
      setCustomer(getCustomerDetailsRes?.data);
      getChannelDetailsByCustomer(
        getCustomerDetailsRes?.data?.inviteId ?? '',
        'C'
      );
    }
  }, [getCustomerDetailsRes]);

  // useEffect(() => {
  //   if (channelDetails) {
  //     console.log('test: channelDetails: ', channelDetails);
  //     dispatch(updateCurrentChannel(channelDetails?.id));
  //   }
  // }, [channelDetails]);

  useEffect(() => {
    if (
      createTrialCampaignRes?.isSuccess &&
      createTrialCampaignRes?.data?.statusCode === 201
    ) {
      getCampaignCustomers(createTrialCampaignRes?.data?.id);
    } else if (createTrialCampaignRes?.data?.statusCode === 500) {
      getAllCustomersReq({
        workspaceId: workspaceId,
        pageNo: 1,
        pageSize: 10,
      });
    }
  }, [createTrialCampaignRes]);

  useEffect(() => {
    if (getAllCustomersRes?.isSuccess && getAllCustomersRes?.data) {
      const customers = getAllCustomersRes?.data?.customers || [];
      if (customers.length > 0) {
        getCustomerDetailsReq({
          workspaceId: workspaceId,
          inviteId: customers[0]?.id || '',
          includeCFA: false,
          includeInactive: false,
        });
      }
    }
  }, [getAllCustomersRes]);

  useEffect(() => {
    scrollToBottom();
    // const latestMsg = groupedMessages[0];
    // if (
    //   typeof latestMsg !== 'string' &&
    //   latestMsg?.message?.reply?.text?.toLowerCase().includes('hi') &&
    //   latestMsg?.message?.template?.action?.button === 'Menu'
    // ) {
    //   dispatch(updateIsJTBDTourActive(true));
    //   dispatch(updateCurrentTooltipId('welcome-menu'));
    //   dispatch(updateCurrentTooltipIndex(0));
    // }
    console.log('test: groupedMessages: ', groupedMessages);
  }, [groupedMessages]);

  useEffect(() => {
    if (messages) {
      if (
        messages[messages?.length - 1]?.message?.template?.action?.parameters
          ?.display_text === 'Order Now'
      ) {
        setContentIndex?.(2);
      }
    }
    console.log('test: messages: ', messages);
  }, [messages]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    chatContainerRef,
    containerRef,
    customer,
    feedbackCount,
    firmName: workspace?.spaceName || '',
    groupedMessages,
    handleKeyDown,
    inputRef,
    menuItems,
    msgText,
    onCloseMenu,
    onCloseWelcomeMenu,
    onMenuClick,
    onMenuItemClick,
    onOpenMenu,
    onOpenWelcomeMenu,
    onSendMenuClick,
    onSendWelcomeMenuOption,
    openMenu,
    openWelcomeMenu,
    publishQuickReply,
    publishReaction,
    publishWhatsappMessage,
    scrollToBottom,
    sendSampleQuestion,
    sendWhatsappMessage,
    setMenuItems,
    setMsgText,
    showAgentWorkingModal,
    showGetOnWhatsappStrip,
    userActions,
    quickReply,
  };
};
